/**
 * FR-RD7 / FR-RD14: every captured chunk is written to IndexedDB before anyone talks to the
 * network, on every web-based recorder — the extension's Meet capture (`RecordingRecord`,
 * used since before this file moved) and the plain web recorder's tab-crash recovery (the
 * `uploadState` store, added for F5). Stop returns once the tail chunk is durable — upload is
 * a concurrent concern, never a gate on ending capture. FR-ST6: this is local-only; objects
 * go to private buckets through the same presigned upload APIs the recorder uses.
 *
 * Shared by `clients/web` and the extension (imported as `@grackle/web/recorder/persist/
 * chunkStore` — see clients/extension/vite.config.ts's alias). Keep this file's existing
 * exports stable; the extension depends on them.
 */
import type { PersistedUploadState } from "../recovery/types";

const DB_NAME = "grackle-meet-capture";
// FR-RD14: v2 adds the `uploadState` store for the plain web recorder's tab-crash recovery.
// Purely additive — existing `chunks`/`recordings` rows and readers are unaffected.
const DB_VERSION = 2;
const CHUNKS = "chunks";
const RECORDINGS = "recordings";
const UPLOAD_STATE = "uploadState";

export interface RecordingRecord {
  id: string;
  title: string;
  /** FR-M2a: set only for Meet; other calls record with no meeting URL. */
  meetUrl: string | null;
  orgId: string;
  mimeType: string;
  contentType: "video/webm" | "video/mp4";
  createdAt: number;
  localComplete: boolean;
  bytesRecorded: number;
  grackleId: string | null;
  watchPath: string | null;
  bookmarks: { atMs: number; label: string }[];
  consentAcknowledged?: boolean;
}

export interface ChunkRecord {
  key: string;
  recordingId: string;
  seq: number;
  blob: Blob;
}

function chunkKey(recordingId: string, seq: number): string {
  return `${recordingId}:${String(seq).padStart(8, "0")}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHUNKS)) {
        const store = db.createObjectStore(CHUNKS, { keyPath: "key" });
        store.createIndex("byRecording", "recordingId", { unique: false });
      }
      if (!db.objectStoreNames.contains(RECORDINGS)) {
        db.createObjectStore(RECORDINGS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(UPLOAD_STATE)) {
        db.createObjectStore(UPLOAD_STATE, { keyPath: "localSessionId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

export async function createRecording(record: RecordingRecord): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(RECORDINGS, "readwrite");
  await reqToPromise(tx.objectStore(RECORDINGS).put(record));
}

export async function putChunk(recordingId: string, seq: number, blob: Blob): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readwrite");
  const row: ChunkRecord = { key: chunkKey(recordingId, seq), recordingId, seq, blob };
  await reqToPromise(tx.objectStore(CHUNKS).put(row));
}

export async function listChunks(recordingId: string): Promise<ChunkRecord[]> {
  const db = await openDb();
  const tx = db.transaction(CHUNKS, "readonly");
  const index = tx.objectStore(CHUNKS).index("byRecording");
  const rows = await reqToPromise(index.getAll(recordingId));
  return rows.sort((a, b) => a.seq - b.seq);
}

export async function updateRecording(
  id: string,
  patch: Partial<Omit<RecordingRecord, "id">>,
): Promise<RecordingRecord | null> {
  const db = await openDb();
  const tx = db.transaction(RECORDINGS, "readwrite");
  const store = tx.objectStore(RECORDINGS);
  const current = await reqToPromise(store.get(id));
  if (!current) return null;
  const next = { ...current, ...patch };
  await reqToPromise(store.put(next));
  return next;
}

export async function getRecording(id: string): Promise<RecordingRecord | null> {
  const db = await openDb();
  const tx = db.transaction(RECORDINGS, "readonly");
  return (await reqToPromise(tx.objectStore(RECORDINGS).get(id))) ?? null;
}

export async function listRecordings(): Promise<RecordingRecord[]> {
  const db = await openDb();
  const tx = db.transaction(RECORDINGS, "readonly");
  const rows = await reqToPromise(tx.objectStore(RECORDINGS).getAll());
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

export async function deleteRecording(id: string): Promise<void> {
  const chunks = await listChunks(id);
  const db = await openDb();
  const tx = db.transaction([CHUNKS, RECORDINGS], "readwrite");
  for (const chunk of chunks) {
    tx.objectStore(CHUNKS).delete(chunk.key);
  }
  tx.objectStore(RECORDINGS).delete(id);
}

export function newRecordingId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `rec_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ---------------------------------------------------------------------------------------
// FR-RD14: the plain web recorder's upload-state store, used by recovery/webAdapter.ts.
// Keyed by `localSessionId` (== the chunk store's `recordingId`), separate from
// `RecordingRecord`/`RECORDINGS` above (that store is the extension's Meet-capture shape —
// meetUrl, bookmarks, consent — and reusing it for a plain web recording would force those
// Meet-only fields onto every recording).
// ---------------------------------------------------------------------------------------

export async function putUploadState(state: PersistedUploadState): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(UPLOAD_STATE, "readwrite");
  await reqToPromise(tx.objectStore(UPLOAD_STATE).put(state));
}

export async function getUploadState(id: string): Promise<PersistedUploadState | null> {
  const db = await openDb();
  const tx = db.transaction(UPLOAD_STATE, "readonly");
  return (await reqToPromise(tx.objectStore(UPLOAD_STATE).get(id))) ?? null;
}

/** Every locally-known web session, oldest `updatedAt` first — recovery processes sessions
 * in order, one at a time (FR-RD14). */
export async function listUploadStates(): Promise<PersistedUploadState[]> {
  const db = await openDb();
  const tx = db.transaction(UPLOAD_STATE, "readonly");
  const rows = await reqToPromise(tx.objectStore(UPLOAD_STATE).getAll());
  return rows.sort((a, b) => a.updatedAt - b.updatedAt);
}

/** Deletes every chunk plus the upload-state row for one local session. Called only once
 * `complete` has succeeded, or a 404 proved an earlier run's `complete` already did (FR-RD14). */
export async function deleteLocalSession(id: string): Promise<void> {
  const chunks = await listChunks(id);
  const db = await openDb();
  const tx = db.transaction([CHUNKS, UPLOAD_STATE], "readwrite");
  for (const chunk of chunks) tx.objectStore(CHUNKS).delete(chunk.key);
  tx.objectStore(UPLOAD_STATE).delete(id);
}
